import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Label } from './label.tsx'

/** The aria-wiring props Field clones onto its control child. */
type ControlProps = {
	id?: string
	'aria-invalid'?: React.AriaAttributes['aria-invalid']
	'aria-describedby'?: string
	'aria-required'?: React.AriaAttributes['aria-required']
}

/** What a function child of Field receives. */
type FieldControlProps = ControlProps & {
	/** The Label's id, for a control the `htmlFor` cannot reach (a group). */
	'aria-labelledby': string
}

interface FieldProps {
	/** Visible label content. */
	label: React.ReactNode
	/** Id wired to both the Label's `htmlFor` and the control's `id`. */
	htmlFor: string
	/** Optional helper text, linked to the control via `aria-describedby`. */
	description?: React.ReactNode
	/** When set, the message is shown and the control becomes `aria-invalid`
	 *  + linked to it via `aria-describedby`. */
	error?: React.ReactNode
	/** Conveys a required field (asterisk + `aria-required` on the control). */
	required?: boolean
	/**
	 * The control element (Input, Textarea, Slider, …), cloned to receive the
	 * wiring. When the control is not the outermost element (a `ui/select`
	 * trigger, an input with an adjacent button, a checkbox group), pass a
	 * function instead: it gets the wiring props to spread onto the control
	 * (the trigger, the input, or the group's `role="group"` wrapper).
	 */
	children:
		| React.ReactElement<ControlProps>
		| ((control: FieldControlProps) => React.ReactNode)
	className?: string
}

/**
 * Field composite — Label + control + optional description + error — that wires
 * `htmlFor`/`id`/`aria-invalid`/`aria-describedby` automatically so consumers
 * stop reinventing error markup. The single child control is cloned to receive
 * the wiring (a function child gets the same props to spread instead): its
 * `id` is set to `htmlFor`, and when `error` is set it gains
 * `aria-invalid` and an `aria-describedby` pointing at the error (and the
 * description, when present). Error text and the required marker use
 * `text-error-text` (`--color-error-text`, an alias of
 * `--foreground-destructive`); the invalid border comes from each control's
 * own `aria-[invalid]` treatment (`--input-invalid`, the invalid-state
 * contract).
 */
const Field = ({
	label,
	htmlFor,
	description,
	error,
	required,
	children,
	className,
}: FieldProps) => {
	const labelId = `${htmlFor}-label`
	const descriptionId = description ? `${htmlFor}-description` : undefined
	const errorId = error ? `${htmlFor}-error` : undefined
	const isRender = typeof children === 'function'
	const childProps = isRender ? {} : children.props
	// The child may already name these ids (conform does); list each once.
	const describedBy =
		[
			...new Set(
				[childProps['aria-describedby'], descriptionId, errorId]
					.join(' ')
					.split(' ')
					.filter(Boolean),
			),
		].join(' ') || undefined

	const controlProps = {
		id: htmlFor,
		'aria-invalid': error ? true : childProps['aria-invalid'],
		'aria-describedby': describedBy,
		'aria-required': required || undefined,
	}
	const control = isRender
		? children({ ...controlProps, 'aria-labelledby': labelId })
		: React.cloneElement(children, controlProps)

	return (
		<div data-slot="field" className={cn('flex flex-col gap-1', className)}>
			<Label id={labelId} htmlFor={htmlFor}>
				{label}
				{required ? (
					<span
						aria-hidden
						data-slot="field-required"
						className="text-error-text"
					>
						{' '}
						*
					</span>
				) : null}
			</Label>
			{control}
			{description ? (
				<p id={descriptionId} className="text-muted-foreground text-body-2xs">
					{description}
				</p>
			) : null}
			{error ? <FieldError id={errorId}>{error}</FieldError> : null}
		</div>
	)
}

/**
 * The error message slot Field renders under its control. A `div`, so the
 * message can be an `ErrorList`. Exported for layouts Field cannot express,
 * such as a checkbox beside its label.
 */
const FieldError = ({ className, ...props }: React.ComponentProps<'div'>) => (
	<div
		data-slot="field-error"
		className={cn('text-error-text text-body-2xs', className)}
		{...props}
	/>
)

export { Field, Field as FormField, FieldError }
export type { FieldControlProps, FieldProps }
